import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '@/utils/api';

// Types for Planner Model
export type PlanType = 'daily' | 'weekly' | 'sprint' | 'exam' | 'custom';
export type PlanStatus = 'active' | 'completed' | 'cancelled';
export type SessionStatus = 'scheduled' | 'in_progress' | 'completed' | 'missed' | 'rescheduled';
export type RoutineFrequency = 'daily' | 'weekdays' | 'weekends' | 'custom';

export interface Plan {
  id: string;
  name: string;
  planType: PlanType;
  startDate: string;
  endDate: string;
  targetMinutes?: number;
  actualMinutes: number;
  status: PlanStatus;
  createdAt: string;
  updatedAt: string;
  sessions?: Session[];
}

export interface Session {
  id: string;
  planId?: string;
  cardId: string;
  cardTitle?: string;
  cardType?: string;
  patternName?: string;
  scheduledDate: string;
  startTime?: string;
  endTime?: string;
  durationMinutes?: number;
  actualStartTime?: string;
  actualEndTime?: string;
  actualDurationMinutes: number;
  status: SessionStatus;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Routine {
  id: string;
  name: string;
  description?: string;
  frequency: RoutineFrequency;
  customDays?: string[];
  preferredTime?: string;
  durationMinutes: number;
  templateCardIds?: string[];
  trackId?: string;
  isActive: boolean;
  streakCount: number;
  longestStreak: number;
  lastCompletedDate?: string;
  createdAt: string;
  updatedAt: string;
}

interface PlannerState {
  plans: Plan[];
  todayPlan: Plan | null;
  weekPlan: Plan | null;
  todaySessions: Session[];
  routines: Routine[];
  isLoading: boolean;
  error: string | null;
  dataVersion: number;
}

const initialState: PlannerState = {
  plans: [],
  todayPlan: null,
  weekPlan: null,
  todaySessions: [],
  routines: [],
  isLoading: false,
  error: null,
  dataVersion: 0,
};

export const fetchTodayPlan = createAsyncThunk(
  'planner/fetchTodayPlan',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/planner/plans/today');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch today plan'
      );
    }
  }
);

export const fetchTodaySessions = createAsyncThunk(
  'planner/fetchTodaySessions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/planner/sessions/today');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch sessions'
      );
    }
  }
);

export const fetchRoutines = createAsyncThunk(
  'planner/fetchRoutines',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/planner/routines');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch routines'
      );
    }
  }
);

export const createSession = createAsyncThunk(
  'planner/createSession',
  async (session: Partial<Session>, { rejectWithValue }) => {
    try {
      const response = await api.post('/planner/sessions', session);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to create session'
      );
    }
  }
);

export const startSession = createAsyncThunk(
  'planner/startSession',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      await api.post(`/planner/sessions/${sessionId}/start`);
      return { sessionId };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to start session'
      );
    }
  }
);

export const completeSession = createAsyncThunk(
  'planner/completeSession',
  async ({ sessionId, notes }: { sessionId: string; notes?: string }, { rejectWithValue }) => {
    try {
      await api.post(`/planner/sessions/${sessionId}/complete`, { notes });
      return { sessionId };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to complete session'
      );
    }
  }
);

export const rescheduleSession = createAsyncThunk(
  'planner/rescheduleSession',
  async ({ sessionId, newDate, newTime }: { sessionId: string; newDate: string; newTime?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/planner/sessions/${sessionId}/reschedule`, {
        scheduledDate: newDate,
        startTime: newTime,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to reschedule session'
      );
    }
  }
);

export const toggleRoutine = createAsyncThunk(
  'planner/toggleRoutine',
  async (routineId: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/planner/routines/${routineId}/toggle`);
      return { routineId, isActive: response.data.isActive };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to toggle routine'
      );
    }
  }
);

const plannerSlice = createSlice({
  name: 'planner',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    invalidatePlannerData: (state) => {
      state.dataVersion = (state.dataVersion || 0) + 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch today plan
      .addCase(fetchTodayPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTodayPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        state.todayPlan = action.payload;
      })
      .addCase(fetchTodayPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch today sessions
      .addCase(fetchTodaySessions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTodaySessions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.todaySessions = action.payload;
      })
      .addCase(fetchTodaySessions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch routines
      .addCase(fetchRoutines.fulfilled, (state, action) => {
        state.routines = action.payload;
      })
      // Create session
      .addCase(createSession.fulfilled, (state, action) => {
        state.todaySessions.push(action.payload);
      })
      // Start session
      .addCase(startSession.fulfilled, (state, action) => {
        const index = state.todaySessions.findIndex(s => s.id === action.payload.sessionId);
        if (index !== -1) {
          state.todaySessions[index].status = 'in_progress';
          state.todaySessions[index].actualStartTime = new Date().toISOString();
        }
      })
      // Complete session
      .addCase(completeSession.fulfilled, (state, action) => {
        const index = state.todaySessions.findIndex(s => s.id === action.payload.sessionId);
        if (index !== -1) {
          state.todaySessions[index].status = 'completed';
          state.todaySessions[index].actualEndTime = new Date().toISOString();
        }
      })
      // Reschedule session
      .addCase(rescheduleSession.fulfilled, (state, action) => {
        const index = state.todaySessions.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          // Remove from today's sessions if rescheduled to different day
          const today = new Date().toISOString().split('T')[0];
          if (action.payload.scheduledDate !== today) {
            state.todaySessions.splice(index, 1);
          } else {
            state.todaySessions[index] = action.payload;
          }
        }
      })
      // Toggle routine
      .addCase(toggleRoutine.fulfilled, (state, action) => {
        const index = state.routines.findIndex(r => r.id === action.payload.routineId);
        if (index !== -1) {
          state.routines[index].isActive = action.payload.isActive;
        }
      });
  },
});

export const { clearError, invalidatePlannerData } = plannerSlice.actions;
export default plannerSlice.reducer;
